import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { WelcomeModal } from '../components/WelcomeModal';

const PENDING_KEY = 'talentfunn_pending_welcome_modal';

function addToLocalStorageArray(key: string, id: string, maxEntries = 100) {
  const existingArray = JSON.parse(localStorage.getItem(key) || '[]');
  if (!existingArray.includes(id)) {
    existingArray.push(id);
  }
  const trimmedArray = existingArray.slice(-maxEntries);
  localStorage.setItem(key, JSON.stringify(trimmedArray));
}

interface WelcomeModalData {
  id: string;
  title: string;
  subtitle?: string;
  message: string;
  icon: string;
  ctaText: string;
  allowClose?: boolean;
  showButton?: boolean;
  textStyles?: unknown;
  triggerType?: string;
}

interface WelcomeModalContextValue {
  openWelcomeModal: (modal: WelcomeModalData) => void;
  closeWelcomeModal: () => void;
}

const WelcomeModalContext = createContext<WelcomeModalContextValue | null>(null);

export function WelcomeModalProvider({ children }: { children: React.ReactNode }) {
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [currentWelcomeModal, setCurrentWelcomeModal] = useState<WelcomeModalData | null>(null);

  const openWelcomeModal = useCallback((modal: WelcomeModalData) => {
    setCurrentWelcomeModal(modal);
    setWelcomeModalOpen(true);
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(modal));
    } catch {
      /* ignore */
    }
  }, []);

  const closeWelcomeModal = useCallback(() => {
    const modal = currentWelcomeModal;
    setWelcomeModalOpen(false);
    setCurrentWelcomeModal(null);
    try {
      sessionStorage.removeItem(PENDING_KEY);
    } catch {
      /* ignore */
    }
    if (modal) {
      if (modal.triggerType === 'first_visit') {
        addToLocalStorageArray('talentfunn_welcome_modals_shown', modal.id, 100);
        localStorage.setItem('talentfunn_visited', 'true');
      } else if (modal.triggerType === 'first_visit_timed') {
        addToLocalStorageArray('talentfunn_welcome_modals_shown', modal.id, 100);
      }
    }
  }, [currentWelcomeModal]);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(PENDING_KEY);
      if (stored) {
        const modal = JSON.parse(stored);
        if (modal?.id) {
          setCurrentWelcomeModal(modal);
          setWelcomeModalOpen(true);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const portal = createPortal(
    <WelcomeModal
      isOpen={welcomeModalOpen}
      modal={currentWelcomeModal}
      onClose={closeWelcomeModal}
    />,
    document.body
  );

  return (
    <WelcomeModalContext.Provider value={{ openWelcomeModal, closeWelcomeModal }}>
      {children}
      {portal}
    </WelcomeModalContext.Provider>
  );
}

export function useWelcomeModal() {
  const ctx = useContext(WelcomeModalContext);
  if (!ctx) {
    throw new Error('useWelcomeModal must be used within WelcomeModalProvider');
  }
  return ctx;
}
